import { IPoint } from '@sparelabs/geography'
import { IListModelQueryParamValues } from '@sparelabs/http-client-utils'

/**
 * Interface for Location custom field values
 * Uses standard @sparelabs/geography types for consistency
 */
export interface ICustomFieldLocationValue {
  address?: string
  location?: IPoint
}

export enum CustomFieldType {
  Text = 'text',
  Number = 'number',
  Toggle = 'toggle',
  SingleChoice = 'singleChoice',
  MultipleChoice = 'multipleChoice',
  Date = 'date',
  Datetime = 'datetime',
  Address = 'address',
  Location = 'location',
  File = 'file',
  Files = 'files',
  Drawing = 'drawing',
  PhoneNumber = 'phoneNumber',

  // Used by surveys.
  NpsScore = 'npsScore',

  // Used by vehicle inspections
  PassFail = 'passFail',
}

export enum CustomFieldStatus {
  Active = 'active',
  Archived = 'archived',
}

export interface ICustomFieldChoice {
  label: string
  value: string
}

export enum CustomFieldVisibility {
  Hidden = 'hidden',
  View = 'view',
  Edit = 'edit',
}

export enum CustomFieldTextType {
  SingleLine = 'singleLine',
  MultiLine = 'multiLine',
}

export interface ICalculationConfig {
  enabled: boolean
  formula: unknown // JSONLogic rule
  description?: string
}
/**
 * Prefix used to identify "Other" choice values in custom fields.
 * "Other" choices allow users to enter free text instead of selecting from predefined options.
 * The format is: `${OTHER_CHOICE_VALUE_PREFIX}:${CustomFieldTextType}`
 */
export const OTHER_CHOICE_VALUE_PREFIX = '__OTHER__'

export interface ICustomFieldPostBody {
  fieldGroupId: string
  type: CustomFieldType
  key: string
  label: string
  isRequired?: boolean
  hint?: string | null
  choices?: ICustomFieldChoice[] | null
  textType?: CustomFieldTextType | null
  calculationConfig?: ICalculationConfig | null
  order?: string
  isAvailableToRiderInterface?: boolean
  isAvailableToDriverInterface?: boolean
  riderInterfaceVisibility?: CustomFieldVisibility
  driverInterfaceVisibility?: CustomFieldVisibility
  isSearchable?: boolean
  isSensitive?: boolean
}

export interface ICustomFieldPatchBody extends Partial<Omit<ICustomFieldPostBody, 'fieldGroupId' | 'key'>> {
  status?: CustomFieldStatus
}

export interface ICustomFieldResponse extends Required<ICustomFieldPostBody> {
  id: string
  createdAt: number
  updatedAt: number
  status: CustomFieldStatus
}

export interface ICustomFieldGroupResponse {
  id: string
}

export interface IListCustomFieldsQueryParams extends IListModelQueryParamValues {
  fieldGroupIds?: string[]
  status?: CustomFieldStatus
}

export enum CustomFieldQueryComparison {
  Equal = 'eq',
  GreaterThan = 'gt',
  LessThan = 'lt',
  GreaterThanOrEqual = 'gte',
  LessThanOrEqual = 'lte',
}

export type CustomFieldFindOptionQuery = Record<string, { comparison: CustomFieldQueryComparison; value: string }>
